import React, { useState } from "react";
import { Paper, TextField, Typography, MenuItem } from "@mui/material";
import { ENGAGEMENT_TYPES } from "../../../../storage/constants";
import { Bar } from "react-chartjs-2";
import { PieChart } from "react-feather";
import SubmissionTypeChip from "../../../../components/SubmissionTypeChip";
import styled from "styled-components";

const BREAKDOWN_OPTIONS = {
  SUBMISSIONS: "submissions",
  COMPLETIONS: "passed",
  PASS_RATE: "passRate",
};

const Wrapper = styled(Paper)`
  padding: 20px;
  min-height: 100px;
  border-radius: 6px;
`;

const ChartWrapper = styled.div`
  width: 100%;
`;

const CustomTextField = styled(TextField)`
  width: 160px;
  margin-right: 18px;
  .MuiSelect-root {
    padding: 8px 32px 8px 8px;
  }

  @media (max-width: 600px) {
    display: none;
  }
`;

const FlexDiv = styled.div`
  display: flex;
  width: 100%;
  align-items: flex-start;
  justify-content: space-between;
`;

const getSubmissionStats = (subs, engagementType) => {
  const employees = {};

  const filteredSubs =
    engagementType === ENGAGEMENT_TYPES.ASSIGNED
      ? subs.filter((sub) => sub.assigned)
      : engagementType === ENGAGEMENT_TYPES.EXTRACREDIT
        ? subs.filter((sub) => !sub.assigned)
        : subs;

  filteredSubs.forEach((sub) => {
    const employeeName = `${sub.first_name} ${sub.last_name}`;
    if (!employees.hasOwnProperty(employeeName)) {
      employees[employeeName] = {
        submissions: 1,
        passed: sub.passed ? 1 : 0,
        failed: !sub.passed ? 1 : 0,
        score: sub.score,
        questions: sub.questions,
        name: employeeName,
      };
    } else {
      employees[employeeName].submissions += 1;
      employees[employeeName].score += sub.score;
      employees[employeeName].questions += sub.questions;
      if (sub.passed) {
        employees[employeeName].passed += 1;
      } else {
        employees[employeeName].failed += 1;
      }
    }
  });
  return employees;
};

const getBestWorst = (employeeObj) => {
  const employeeArr = Object.values(employeeObj).map((employee) => ({
    ...employee,
    passRate: Number(
      ((employee.passed / employee.submissions) * 100).toFixed(2),
    ),
    averageScore: Number(
      ((employee.score / employee.questions) * 100).toFixed(2),
    ),
  }));

  return {
    employeeArr,
  };
};

const getIsPercentage = (breakdown) =>
  breakdown === BREAKDOWN_OPTIONS.PASS_RATE;

const getChartHeight = (num) => {
  if (num < 10) return 320;
  if (num > 40) return 500;
  return 420;
};

const getCleanName = (name) => {
  if (name === BREAKDOWN_OPTIONS.SUBMISSIONS) {
    return " Submissions";
  }
  if (name === BREAKDOWN_OPTIONS.PASS_RATE) {
    return "Pass Rate";
  }
  if (name === BREAKDOWN_OPTIONS.COMPLETIONS) {
    return "Completions";
  }
  return "None";
};

const BreakdownChart = ({ submissions, engagementType }) => {
  const employees = getSubmissionStats(submissions, engagementType);
  const [breakdown, setBreakdown] = useState(BREAKDOWN_OPTIONS.COMPLETIONS);

  // if (Object.keys(employees).length < 3) return null;

  const { employeeArr } = getBestWorst(employees);
  const numEmployees = employeeArr.length;

  const newBreakdownData = employeeArr.map((employee) => ({
    name: employee.name,
    metric: employee[breakdown],
  }));

  const newAverageMean = Math.floor(
    newBreakdownData.reduce((tot, next) => {
      return (tot += next.metric);
    }, 0) / newBreakdownData.length,
  );

  const versusMean = newBreakdownData
    .map((data) => ({
      ...data,
      vsMean: Math.floor(Number(data.metric - newAverageMean)),
    }))
    .sort((first, second) => second.metric - first.metric);

  const chartHeight = getChartHeight(numEmployees);

  const cleanName = getCleanName(breakdown);

  const isPercentage = getIsPercentage(breakdown);

  const options = {
    maintainAspectRatio: false,
    cornerRadius: 2,
    indexAxis: "y",
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: () => "",
          title: (context) => `${context[0].label}`,
          beforeBody: (context) =>
            `${cleanName}: ${context[0].raw}${isPercentage ? "%" : ""}`,
        },
      },
    },
    scales: {
      x: {
        suggestedMax: 100,
        title: {
          display: true,
          text: () => {
            if (isPercentage) {
              return `${cleanName} %`;
            }
            return cleanName;
          },
        },
        ticks: {
          callback: function (value) {
            if (isPercentage) {
              return `${value}%`;
            }
            return value;
          },
        },
      },
    },
  };

  const chartData = {
    labels: versusMean.map((val) => val.name),
    datasets: [
      {
        data: versusMean.map((val) => val.metric),
        borderColor: "black",
        borderRadius: 6,
        borderWidth: 1,
        barPercentage: 0.9,
        backgroundColor: "#337ab7",
      },
    ],
  };

  return (
    <Wrapper>
      <FlexDiv>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 10,
          }}
        >
          <PieChart color="rgb(51, 122, 183)" height={11} width={11} />
          <Typography style={{ fontSize: 16, fontWeight: "600" }}>
            {cleanName} Breakdown
          </Typography>
          <SubmissionTypeChip small submissionType={engagementType} />
        </div>
        <CustomTextField
          select
          size="small"
          variant="outlined"
          value={breakdown}
          onChange={(e) => setBreakdown(e.target.value)}
        >
          <MenuItem value={BREAKDOWN_OPTIONS.COMPLETIONS}>Completions</MenuItem>
          <MenuItem value={BREAKDOWN_OPTIONS.SUBMISSIONS}>Submissions</MenuItem>
          <MenuItem value={BREAKDOWN_OPTIONS.PASS_RATE}>Pass Rate</MenuItem>
        </CustomTextField>
      </FlexDiv>
      <ChartWrapper>
        <Bar data={chartData} options={options} height={chartHeight} />
      </ChartWrapper>
    </Wrapper>
  );
};

export default BreakdownChart;
